import React, { memo, useState, useRef } from 'react';
import OpenInViewDropdown from '../../../../shared/OpenInViewDropdown';
import ConditionsDropdown from '../../../../shared/ConditionsDropdown';
import { getAllConditionTaskIds } from '../../../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * ActionButtons - Hover action buttons for TodoItem
 * Shows: Open in view, Attachments, Conditions, Add nested mission, Archive/Unarchive, Delete
 */
const ActionButtons = memo(({
  task,
  allTasks = [],
  visible,
  onFocusTask,
  onOpenAttachments,
  onAddSubtask,
  onArchive,
  onUnarchive,
  onDeleteClick,
  onConditionsChange,
  // View navigation
  currentView,
  customViews,
  activeCustomViewId,
  sourceCampaignId = null, // For nested items - the campaign where this item belongs
  // Element visibility
  elementVisibility = {},
  showAttachments = false,
  // For parent items with nested sprints
  isParent = false,
  onAddMissionToSprint,
}) => {
  // Check visibility settings (defaults to true if not set)
  const showOpenInView = elementVisibility.openInViewButton !== false;
  const showArchiveButton = elementVisibility.archiveButton !== false;
  const showDeleteButton = elementVisibility.deleteButton !== false;
  const [isConditionsOpen, setIsConditionsOpen] = useState(false);
  const conditionsButtonRef = useRef(null);

  const conditionIds = getAllConditionTaskIds(task.unlockConditions);
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
  const conditionsCount = conditionIds.length || deps.length;
  const hasConditions = conditionsCount > 0;
  return (
    <div className={`todo-item__actions ${visible ? 'todo-item__actions--visible' : ''}`}>
      {/* Open in view dropdown */}
      {showOpenInView && (
        <OpenInViewDropdown
          taskId={task.id}
          sourceCampaignId={sourceCampaignId}
          currentView={currentView}
          customViews={customViews}
          activeCustomViewId={activeCustomViewId}
          variant="icon"
          className="todo-item__action"
        />
      )}

      {/* Attachments */}
      {showAttachments && (
        <button 
          className={`todo-item__action ${(task.attachments?.length > 0) ? 'todo-item__action--has-attachments' : ''}`}
          onClick={(e) => { e.stopPropagation(); onOpenAttachments?.(task.id); }}
          title={task.attachments?.length > 0 ? `${task.attachments.length} attachment${task.attachments.length !== 1 ? 's' : ''}` : "Attachments"}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
          {task.attachments?.length > 0 && (
            <span className="todo-item__action-count">{task.attachments.length}</span>
          )}
        </button>
      )}

      {/* Conditions (Unlock rules) */}
      {onConditionsChange && (
        <>
          <button
            ref={conditionsButtonRef}
            className={`todo-item__action ${hasConditions ? 'todo-item__action--has-conditions' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsConditionsOpen(!isConditionsOpen);
            }}
            title={hasConditions ? `${conditionsCount} unlock condition${conditionsCount !== 1 ? 's' : ''}` : "Set unlock conditions"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <path d="M9 15l2 2 4-4" />
            </svg>
            {hasConditions && (
              <span className="todo-item__action-count">{conditionsCount}</span>
            )}
          </button>
          <ConditionsDropdown
            task={task}
            allTasks={allTasks}
            onConditionsChange={onConditionsChange}
            isOpen={isConditionsOpen}
            onClose={() => setIsConditionsOpen(false)}
          />
        </>
      )}

      {/* Add nested mission (subtask) - only for non-parent items */}
      {/* Parent items with nested sprints use the popup "Add New" button instead */}
      {!isParent && (
        <button 
          className="todo-item__action todo-item__action--add"
          onClick={(e) => { e.stopPropagation(); onAddSubtask?.(task.id); }}
          title="Add nested mission"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      )}

      {/* Archive/Unarchive */}
      {showArchiveButton && (
        task.archived ? (
          <button 
            className="todo-item__action todo-item__action--restore"
            onClick={(e) => { e.stopPropagation(); onUnarchive?.(task.id); }}
            title="Restore from archive"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 8 3 21 21 21 21 8" />
              <rect x="1" y="3" width="22" height="5" />
              <polyline points="10 12 14 16 10 20" />
              <line x1="14" y1="16" x2="6" y2="16" />
            </svg>
          </button>
        ) : (
          <button 
            className="todo-item__action"
            onClick={(e) => { e.stopPropagation(); onArchive?.(task.id); }}
            title="Archive"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="21 8 21 21 3 21 3 8" />
              <rect x="1" y="3" width="22" height="5" />
              <line x1="10" y1="12" x2="14" y2="12" />
            </svg>
          </button>
        )
      )}

      {/* Delete */}
      {showDeleteButton && (
        <button 
          className="todo-item__action todo-item__action--delete"
          onClick={(e) => { e.stopPropagation(); onDeleteClick(); }}
          title="Delete"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      )}
    </div>
  );
});

ActionButtons.displayName = 'ActionButtons';

export default ActionButtons;
